import * as React from "react";
import { useHistory, useLocation } from "react-router";
import { Grid, Loader } from "semantic-ui-react";

import { ConfirmationModal } from "@/components/Modals/ConfirmationModal";
import { PageActionProps, PageTitle } from "@/components/PageTitle";
import { SideBar } from "@/components/SideBar";
import { TitleSection } from "@/components/TitleSection";
import { Media, UploadProgressMap, WebComponents } from "../../../interfaces";
import { useAccountSettings, URLS, useSocialCoachSession } from "../../../utils";
import { VideoProject, VideoProjectServices } from "@/services/bedrock";

import { VideoProjectForm } from "./components/VideoProjectForm";
import { modalReducer } from "./reducer";

import { WhiteContainer } from "@/styling/baseStyle";
import "./styles.scss";
import { ProjectFileUploader } from "@/utils/projectFileUploader";
import { UploadProgressModal } from "@/components/UploadProgressModal";
import { Dialog } from "@/components/ui";
import { Loader2 } from "lucide-react";

type Props = {
  embedded?: boolean;
};

const VideoProjectFC: React.FC<Props> = ({ embedded }) => {
  const history = useHistory();
  const location = useLocation();
  const { coachId } = useSocialCoachSession();
  const { hasWritePermission } = useAccountSettings();
  const writePermission = hasWritePermission(WebComponents.VIDEO_CATALYST);
  const [isValid, setIsValid] = React.useState(true);

  const [state, dispatch] = React.useReducer(modalReducer, {
    open: false,
    status: "ACTIVE",
    duplicated: false,
    action: "close",
  });

  // Properties
  const [loading, setLoading] = React.useState<boolean>(false);
  const [submittingProject, setSubmittingProject] = React.useState<boolean>(false);
  const [formErrorMessages, setFormErrorMessages] = React.useState<string[] | undefined>(undefined);
  const [uploadingFiles, setUploadingFiles] = React.useState(false);
  const [uploadProgressMap, setUploadProgressMap] = React.useState<UploadProgressMap>();
  const [videoProject, setVideoProject] = React.useState<any>({});

  // Location params
  const searchParams = new URLSearchParams(location.search);
  const preloadedPlayerId = searchParams.get("playerId");

  const createVideoProject = async (): Promise<any> => {
    if (videoProject) {
      const {
        playerId,
        allowShortLink,
        allowComplianceDisclaimer,
        allowHashtags,
        isAutoPost,
        originalMediaGroup,
        withMessage,
        preferredPostDate,
        hashtags,
        initialMessages,
        preferences,
      } = videoProject;

      const socialNetworkIds = Object.keys(videoProject.socialNetworkMessages).filter(
        key => videoProject.socialNetworkMessages[key]
      );

      const uploader = new ProjectFileUploader("videoProject", playerId);
      const uploadMap: UploadProgressMap = {};
      originalMediaGroup.media.forEach((mediaObj: Media) => {
        uploadMap[mediaObj.id] = { progress: 0, task: undefined };
      });
      setUploadProgressMap(uploadMap);
      setUploadingFiles(true);
      originalMediaGroup.media = await uploader.prepareMedia(originalMediaGroup.media, (fileId, progress) => {
        setUploadProgressMap(map => {
          return { ...map, [fileId]: { progress, task: undefined } };
        });
      });
      setUploadingFiles(false);
      setSubmittingProject(true);

      VideoProjectServices.create({
        playerId,
        messageToPost: withMessage,
        originalMedia: originalMediaGroup.media,
        hashtags,
        isAutoPost,
        preferredPostDate,
        allowShortLink,
        allowCompliance: allowComplianceDisclaimer,
        initialMessages: (initialMessages || [])
          .map((message: { content?: string }) => message.content)
          .filter(Boolean),
        allowPlayerHashtags: allowHashtags,
        socialNetworkIds,
        preferences: preferences
          ? {
              captions: preferences.captions,
              emojis: preferences.emojis,
              bRoll: preferences.broll,
              music: preferences.music,
              captionColors: preferences.captionColors,
              tone: preferences.tone,
              musicGenre: preferences.musicGenre,
            }
          : undefined,
        source: VideoProject.Source.COACH_WEB,
      })
        .then(newProject => {
          setSubmittingProject(false);
          history.replace(URLS.coach.videoProject.replace(":id", `${newProject.id}`));
        })
        .catch(error => {
          setSubmittingProject(false);
          setFormErrorMessages([error.substring(0, 100)]);
        });
    }
  };

  const cancelAction = () => {
    dispatch({ type: "open_cancel", status: "ACTIVE" });
  };

  const confirmationModal = (): JSX.Element => {
    const modalTitle = "Are you sure you want to discard this project?";
    return (
      <ConfirmationModal
        title={!state.duplicated ? modalTitle : undefined}
        message={""}
        openConfirmationModal={state.open}
        onClose={() => dispatch({ type: "close" })}
        okHandler={() => {
          dispatch({ type: "close" });
          history.push(URLS.coach.videoProjects);
        }}
        rejectHandler={() => dispatch({ type: "close" })}
      />
    );
  };

  const customLinkSection = (disableButtons: boolean): JSX.Element => (
    <Grid.Column width={7} floated="right" className="skip-link noMarginRight">
      <span className={"rightPadding"} data-elm-id={"videoProjectPageCancelBtn"} onClick={cancelAction}>
        Cancel
      </span>
    </Grid.Column>
  );

  const content = (): JSX.Element => {
    return (
      <Grid.Row columns={1} className={"leftPadding"}>
        {confirmationModal()}
        <UploadProgressModal open={uploadingFiles} progressMap={uploadProgressMap} />

        <Dialog open={submittingProject} onOpenChange={undefined} size="sm" disableEscapeDismissal={true}>
          <Dialog.Header>
            <Dialog.Title className="flex items-center gap-1.5 text-slate-800 text-sm font-semibold select-none">
              <Loader2 className="animate-spin size-4 text-indigo-600 shrink-0" />
              <span>Submitting Project</span>
            </Dialog.Title>
          </Dialog.Header>

          <Dialog.Content className="p-5 flex flex-col items-center justify-center gap-4 text-center">
            <div className="text-xs text-slate-600 font-medium">
              Please wait while we submit your video project changes. This may take a few moments.
            </div>
          </Dialog.Content>
        </Dialog>

        <VideoProjectForm
          disabled={!writePermission}
          modeEditable={false}
          videoProject={videoProject}
          updatingHandler={setVideoProject}
          handleValidation={setIsValid}
          pageErrors={formErrorMessages}
          coachId={coachId!!}
          playerId={preloadedPlayerId}
        />
      </Grid.Row>
    );
  };

  const actions: PageActionProps[] = [];

  if (writePermission) {
    actions.push({
      text: "Submit for Editing",
      onClick: createVideoProject,
      disabled: uploadingFiles || submittingProject || !isValid,
    });
    actions.push({
      text: "Cancel",
      onClick: cancelAction,
      disabled: uploadingFiles || submittingProject || !isValid,
    });
  }

  if (embedded) {
    return (
      <div>
        <PageTitle
          title="Video Catalyst Project"
          actions={actions}
          breadcrumbs={[{ name: "Library", link: URLS.coach.videoProjects }]}
        />
        {content()}
      </div>
    );
  } else {
    return (
      <WhiteContainer>
        <SideBar history={history} />
        {loading ? (
          <Loader active size="large" />
        ) : (
          <Grid columns={1} className={"newProjectContentSection contentSection"}>
            <TitleSection
              title="Video Catalyst Project"
              customLinkSection={customLinkSection(uploadingFiles || submittingProject)}
              customNextLabel="Submit for Editing"
              handleNextAction={() => createVideoProject()}
              disableButtons={uploadingFiles || submittingProject || !isValid || !writePermission}
              titleSize={8}
              buttonsSize={8}
              nextButtonSize={7}
              showLinkOption={writePermission}
              showNextOption={writePermission}
            />
            {content()}
          </Grid>
        )}
      </WhiteContainer>
    );
  }
};

export const VideoProjectPage = VideoProjectFC;
